import sortBy from "lodash.sortby";
import { useMemo } from "react";

import {
  Avatar,
  IconButton,
  Key,
  SkeletonBlock,
  SkeletonText,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from "@zapier/design-system";

import { useTranslations } from "@zapier/i18n";
import styles from "./FolderSharingModalList.module.css";
import { HydratedFolderShare } from "@zapier/client";

interface Props {
  members: HydratedFolderShare[];
  isLoading: boolean;
  onRemove: (id: Key) => void;
}

export function FolderSharingModalList({
  members,
  isLoading,
  onRemove,
}: Props) {
  const t = useTranslations("folder-sharing-modal-list");

  // Sort members and teams alphabetically in modal list
  const sortedMembers = useMemo(() => {
    return sortBy(members, (member) => {
      if (member.id.startsWith("owner")) {
        return 0;
      }

      return member.member?.fullName || member.team?.name;
    });
  }, [members]);

  // Render skeleton loading state during initial load
  if (members.length === 0 && isLoading) {
    return (
      <div>
        <ul className={styles.root}>
          <li>
            <div className={styles.loading}>
              <SkeletonBlock height={24} width={24} />
              <SkeletonText lines={1} size="body" />
            </div>
          </li>
        </ul>
      </div>
    );
  }
  return (
    <div>
      <ul className={styles.root}>
        {sortedMembers.map(({ id, shareType, member, team, account }) => {
          const memberCount = team?.memberCount || account?.memberCount || 0;
          const memberCountText = memberCount
            ? t("labels.member-count", { count: memberCount })
            : "";

          const name =
            member?.fullName ||
            team?.name ||
            (account?.name ? t("labels.everyone") : "");
          const url = member?.photoUrl || team?.logo || account?.logoUrl || "";
          const description = member?.email || memberCountText || "";
          const role = shareType === "owner" ? t("labels.owner") : "";

          const isItemLoading = isLoading || (!member && !team && !account);

          return (
            <li key={id}>
              {isItemLoading ? (
                <div className={styles.loading}>
                  <SkeletonBlock height={24} width={24} />
                  <SkeletonText lines={1} size="body" />
                </div>
              ) : (
                <div>
                  <Avatar name={name} url={url} />
                  <Text tag="p" type="Body1">
                    <strong>{name}</strong> - {description}
                    {role ? ` (${role})` : ""}
                  </Text>
                </div>
              )}
              {shareType !== "owner" && !isLoading && (
                <Tooltip content="Remove" placement="left">
                  <TooltipFocusableTrigger>
                    <IconButton
                      name="formX"
                      aria-label="Remove"
                      size="small"
                      variant="ghost"
                      onPress={() => onRemove(id)}
                    />
                  </TooltipFocusableTrigger>
                </Tooltip>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
